import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SelectBusca } from "@/components/padroes";
import { api } from "@/lib/api";
import { formatCents, maskReaisInput, parseReaisInput } from "@/lib/format";
import { LANCAMENTOS_CLT, type LancamentoClt, type TipoLancamentoClt } from "@/data/folha";
import type { User } from "@/data/mock";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, Plus, Trash2 } from "lucide-react";

interface Props {
  folhaId: string;
  editavel: boolean;
  colaboradores: User[];
  /** Chamado depois de cada alteração: os holerites foram recalculados. */
  onChange: () => void;
}

const TIPOS = Object.keys(LANCAMENTOS_CLT) as TipoLancamentoClt[];

const quantidadeTexto = (l: LancamentoClt) => {
  const u = LANCAMENTOS_CLT[l.tipo].unidade;
  if (u === "valor") return l.valor != null ? formatCents(l.valor) : "—";
  const q = (l.quantidade ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  return `${q} ${u === "horas" ? "h" : l.quantidade === 1 ? "dia" : "dias"}`;
};

/** "20:38" ou "20,5" → horas decimais. */
function parseQuantidade(s: string, unidade: "horas" | "dias"): number | null {
  const t = s.trim();
  const hm = t.match(/^(\d+):(\d{1,2})$/);
  if (unidade === "horas" && hm) return Number(hm[1]) + Number(hm[2]) / 60;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Lançamentos variáveis do mês dos CLT. As planilhas de comissões e da Amil
 * continuam entrando sozinhas; aqui vai o que não vem de planilha.
 */
export const LancamentosCltPainel = ({ folhaId, editavel, colaboradores, onChange }: Props) => {
  const [lista, setLista] = useState<LancamentoClt[]>([]);
  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [f, setF] = useState({
    colaboradorId: "",
    tipo: "horas_extras_50" as TipoLancamentoClt,
    quantidade: "",
    valor: "",
    descricao: "",
    inss: true,
    irrf: true,
    fgts: true,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  const load = useCallback(() => {
    api
      .get<LancamentoClt[]>(`/clt/folhas/${folhaId}/lancamentos`)
      .then((l) => {
        setLista(l);
        if (l.length > 0) setAberto(true);
      })
      .catch(() => undefined);
  }, [folhaId]);
  useEffect(load, [load]);

  const clts = useMemo(
    () => colaboradores.filter((c) => c.vinculo === "clt").sort((a, b) => a.name.localeCompare(b.name)),
    [colaboradores],
  );
  const unidade = LANCAMENTOS_CLT[f.tipo].unidade;
  const outro = f.tipo === "outro_provento" || f.tipo === "outro_desconto";

  const adicionar = async () => {
    if (!f.colaboradorId) {
      toast.error("Escolha o colaborador.");
      return;
    }
    let quantidade: number | null = null;
    let valor: number | null = null;
    if (unidade === "valor") {
      try {
        valor = f.valor.trim() ? parseReaisInput(f.valor) : 0;
      } catch {
        valor = 0;
      }
      if (!valor) {
        toast.error("Informe o valor.");
        return;
      }
    } else {
      quantidade = parseQuantidade(f.quantidade, unidade);
      if (!quantidade) {
        toast.error(unidade === "horas" ? "Informe as horas (ex.: 10,5 ou 10:30)." : "Informe os dias.");
        return;
      }
    }
    setSalvando(true);
    try {
      await api.post(`/clt/folhas/${folhaId}/lancamentos`, {
        colaboradorId: f.colaboradorId,
        tipo: f.tipo,
        quantidade,
        valor,
        descricao: f.descricao.trim() || null,
        incideInss: outro && f.inss,
        incideIrrf: outro && f.irrf,
        incideFgts: outro && f.fgts,
      });
      toast.success("Lançado — holerite recalculado");
      setF((x) => ({ ...x, quantidade: "", valor: "", descricao: "" }));
      load();
      onChange();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao lançar", { duration: 10_000 });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (id: string) => {
    try {
      await api.delete(`/clt/folhas/${folhaId}/lancamentos/${id}`);
      load();
      onChange();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao excluir");
    }
  };

  if (!editavel && lista.length === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <button
          type="button"
          className="flex w-full items-center gap-1.5 text-left text-sm font-medium"
          onClick={() => setAberto((v) => !v)}
        >
          {aberto ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Lançamentos do mês ({lista.length})
          <span className="font-normal text-muted-foreground">
            — horas extras, faltas, prêmios, consignado… Comissões e Amil vêm das planilhas.
          </span>
        </button>

        {aberto && (
          <>
            {lista.length > 0 && (
              <div className="divide-y rounded-md border text-sm">
                {lista.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                    <div className="min-w-0">
                      <span className="font-medium">{l.colaborador.name}</span>{" "}
                      <span className="text-muted-foreground">
                        · {LANCAMENTOS_CLT[l.tipo].rotulo}
                        {l.descricao ? ` · ${l.descricao}` : ""}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="tabular-nums">{quantidadeTexto(l)}</span>
                      {editavel && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => void excluir(l.id)} aria-label="Excluir lançamento">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {editavel && (
              <div className="grid gap-3 rounded-md border bg-muted/20 p-3 sm:grid-cols-12">
                <div className="space-y-1.5 sm:col-span-4">
                  <Label>Colaborador</Label>
                  <SelectBusca
                    value={f.colaboradorId}
                    onChange={(v) => set("colaboradorId", v)}
                    placeholder="Escolha"
                    placeholderBusca="Buscar colaborador..."
                    vazio="Nenhum colaborador encontrado."
                    opcoes={clts.map((c) => ({ value: c.id, label: c.name }))}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-3">
                  <Label>Tipo</Label>
                  <Select value={f.tipo} onValueChange={(v) => set("tipo", v as TipoLancamentoClt)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {LANCAMENTOS_CLT[t].rotulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>{unidade === "valor" ? "Valor" : unidade === "horas" ? "Horas" : "Dias"}</Label>
                  {unidade === "valor" ? (
                    <Input value={f.valor} onChange={(e) => set("valor", maskReaisInput(e.target.value))} placeholder="0,00" inputMode="numeric" className="tabular-nums" />
                  ) : (
                    <Input value={f.quantidade} onChange={(e) => set("quantidade", e.target.value)} placeholder={unidade === "horas" ? "10:30" : "1"} />
                  )}
                </div>
                <div className="space-y-1.5 sm:col-span-3">
                  <Label>{f.tipo === "consignado" ? "Nº do contrato" : "Descrição"}</Label>
                  <Input value={f.descricao} onChange={(e) => set("descricao", e.target.value)} placeholder={outro ? "Obrigatória" : "Opcional"} />
                </div>
                {outro && (
                  <div className="flex flex-wrap items-center gap-4 text-sm sm:col-span-9">
                    <span className="text-muted-foreground">Incide em:</span>
                    {(
                      [
                        ["inss", "INSS"],
                        ["irrf", "IRRF"],
                        ["fgts", "FGTS"],
                      ] as const
                    ).map(([k, r]) => (
                      <label key={k} className="flex items-center gap-1.5">
                        <Checkbox checked={f[k]} onCheckedChange={(v) => set(k, v === true)} />
                        {r}
                      </label>
                    ))}
                  </div>
                )}
                <div className={`flex items-end ${outro ? "sm:col-span-3" : "sm:col-span-12 justify-end"}`}>
                  <Button onClick={() => void adicionar()} disabled={salvando} className={outro ? "w-full" : ""}>
                    {salvando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
                    Lançar
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
